import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { useNow } from "@/lib/hooks";
import { scrollToId } from "@/lib/lenis";
import { Lockup } from "@/components/Brand";
import { LangToggle } from "@/components/Nav";

// Index vertical du « programme de gala » : 12 entrées, libellés issus du contenu.
export const useProgramIndex = () => {
  const c = useContent();
  return [
    { id: "top", label: c.nav.tabs[0].label },
    { id: c.essence.id, label: c.nav.items[0].label },
    { id: c.why.id, label: c.why.eyebrow },
    { id: c.who.id, label: c.who.eyebrow },
    { id: c.categories.id, label: c.nav.items[1].label },
    { id: c.journey.id, label: c.nav.items[2].label },
    { id: c.dates.id, label: c.nav.items[3].label },
    { id: c.jury.id, label: c.nav.items[4].label },
    { id: c.criteria.id, label: c.nav.items[5].label },
    { id: "selection", label: c.finalists.eyebrow },
    { id: c.ecosystem.id, label: c.ecosystem.eyebrow },
    { id: c.finalCall.id, label: c.contactPage.eyebrow },
  ];
};

const MiniCountdown = () => {
  const c = useContent();
  const t = c.ui.countdown;
  const now = useNow(1000);
  const diff = Math.max(0, new Date(c.site.openingDate).getTime() - now);
  if (diff === 0) return <p className="font-serif text-lg italic text-blush" data-testid="sidebar-countdown-open">{t.open}</p>;
  const s = Math.floor(diff / 1000);
  const parts = [
    [Math.floor(s / 86400), t.days],
    [Math.floor((s % 86400) / 3600), t.hours],
    [Math.floor((s % 3600) / 60), t.minutes],
    [s % 60, t.seconds],
  ];
  return (
    <div data-testid="sidebar-countdown">
      <p className="text-[0.75rem] uppercase tracking-[0.28em] text-copper">{t.label}</p>
      <div className="mt-3 grid grid-cols-4 gap-2" role="timer" aria-live="off">
        {parts.map(([n, label], i) => (
          <div key={i}>
            <div className="font-serif text-[1.5rem] font-light leading-none tabular-nums text-white">{String(n).padStart(2, "0")}</div>
            <div className="mt-1.5 text-[0.75rem] uppercase tracking-[0.12em] text-white/70">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const c = useContent();
  const loc = useLocation();
  const items = useProgramIndex();
  const [active, setActive] = useState("top");
  const onHome = loc.pathname === "/";

  // Indicateur « section active » (uniquement sur la page d'accueil).
  useEffect(() => {
    if (!onHome) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-38% 0px -55% 0px" }
    );
    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHome, loc.key]);

  const go = (id) => (e) => {
    if (!onHome) return; // hors accueil : le lien /#id laisse le ScrollManager faire le défilement
    e.preventDefault();
    scrollToId(id);
  };

  return (
    <aside
      className="fixed inset-y-0 start-0 z-50 hidden w-[340px] flex-col justify-between overflow-y-auto border-e border-copper/25 bg-[#141011] px-9 py-7 text-white lg:flex"
      data-testid="sidebar"
    >
      <div>
        <Link to="/" aria-label={c.a11y?.home} data-testid="sidebar-logo" className="inline-block">
          <Lockup height={46} />
        </Link>
        <nav aria-label={c.ui.index} className="mt-8" data-testid="sidebar-index">
          <p className="text-[0.75rem] uppercase tracking-[0.35em] text-copper">{c.ui.index}</p>
          <ul className="mt-4 space-y-0">
            {items.map((it, i) => {
              const current = onHome && active === it.id;
              return (
                <li key={it.id}>
                  <Link
                    to={`/#${it.id}`}
                    onClick={go(it.id)}
                    aria-current={current ? "true" : undefined}
                    data-testid={`sidebar-item-${it.id}`}
                    className={`group flex items-baseline gap-4 py-[5px] transition-colors duration-300 ${current ? "text-white" : "text-white/60 hover:text-white"}`}
                  >
                    <span className={`w-7 shrink-0 font-sans text-[0.75rem] tabular-nums tracking-[0.18em] transition-colors duration-300 ${current ? "text-blush" : "text-copper/70 group-hover:text-copper"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`h-px w-4 shrink-0 self-center transition-all duration-500 ${current ? "w-7 bg-blush" : "bg-copper/40 group-hover:bg-copper"}`} aria-hidden="true" />
                    <span className="font-serif text-[1.02rem] leading-snug">{it.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
      <div className="mt-6 space-y-5 border-t border-copper/25 pt-5">
        <MiniCountdown />
        <Link
          to={c.nav.cta.to}
          data-testid="sidebar-apply"
          className="group flex items-center justify-center gap-2 bg-plum px-6 py-3.5 text-[0.75rem] font-semibold uppercase tracking-[0.28em] text-white transition-colors duration-300 hover:bg-[#C8102E]"
        >
          {c.nav.cta.label}
          <ArrowUpRight size={15} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <LangToggle />
      </div>
    </aside>
  );
};
